import React, { useEffect, useState } from 'react';
import { IListingData } from '@models/listing';
import StaticLayout from '@components/layout/StaticLayout';
import { useLazyQuery, useQuery } from '@apollo/client';
import { getProviderListQuery, getProvidersLovsQuery } from './queries';
import { getVariables, mapToListingData, toLookups } from './utils';
import { filterSectionsContent, headers } from './content';
import { IListingFilterWidgetSection } from '@components/widgets/listing-filter-widget';
import ListingFilterWidget from '@components/widgets/listing-filter-widget/ListingFilterWidget';
import { IAbstractRecord } from '@models';
import { isEqual } from 'lodash';
import { getFilter, setFilter } from '@utils/filter-utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { toast } from 'react-toastify';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { useAppSelector } from '@redux/hooks';
import ProviderDrawer from '../provider-drawer/ProviderDrawer';
import ListingWidget from '@components/widgets/listing-widget/ListingWidget';
import { IEnhancedMenuItem, IFilterModel } from '@components/table/types';

const PAGE_CONTEXT = 'Providers';
const FILTER_SESSION_KEY = 'providersFilter';

const ProvidersPage: React.FC = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  const initialFilterValues =
    Object.keys(getFilter(FILTER_SESSION_KEY)).length > 0
      ? getFilter(FILTER_SESSION_KEY)
      : {
          isCompany: false,
          isLocal: false,
          selectedTypes: [],
          providerStatus: [],
          createdOn: [],
          effectiveDate: [],
        };

  const [filterValues, setFilterValues] = useState<IFilterModel>({
    namedFilters: initialFilterValues,
    pagination: {
      pageSize: 10,
      pageNumber: 1,
    },
  });

  const [filterSections, setFilterSections] =
    useState<IListingFilterWidgetSection[]>();

  const [tableData, setTableData] = useState<IListingData>({
    pagedItems: {},
    pageSize: 10,
    pageNumber: 1,
    totalCount: 0,
  });

  const [getProviderListLazy, { loading }] = useLazyQuery(getProviderListQuery);
  const { data: lovsData, loading: lovsLoading } = useQuery(
    getProvidersLovsQuery
  );

  const user = useAppSelector((state) => state.user);
  const isAdmin = user.userRoles.includes('Insurance-Admin');
  const actions: IEnhancedMenuItem[] = isAdmin
    ? [
        {
          name: 'new',
          title: '+ New',
          onClick: () => {
            setIsDrawerOpen(true);
          },
        },
      ]
    : [];

  const getListingData = async (filter = filterValues) => {
    if (!filter) {
      return {};
    }

    const variables = getVariables(filter);
    return getProviderListLazy({
      variables: variables,
      errorPolicy: 'all',
    })
      .then(({ data }) => {
        if (data) {
          const tableData = mapToListingData(data);

          setTableData({
            ...tableData,
            pageNumber: tableData.pageNumber,
            pageSize: tableData.pageSize,
            totalCount: tableData.totalCount,
          });
        }
      })
      .catch(() => {
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
      });
  };

  useEffect(() => {
    if (lovsData) {
      const lovs = toLookups(lovsData);
      const newFilterSections = filterSectionsContent(
        lovs,
        filterValues.namedFilters
      );
      setFilterSections(newFilterSections);
      setFilterValues(filterValues);
    }
  }, [lovsData]);

  function handlePageChange(filterModel = filterValues): Promise<void> {
    return new Promise<void>(() => getListingData(filterModel));
  }

  const onFilterUpdate = async (filters: IAbstractRecord) => {
    const newFilters = {
      ...filterValues,
      namedFilters: filters,
    };
    if (isEqual(newFilters, filterValues)) {
      return;
    }
    setFilter(filters, FILTER_SESSION_KEY);
    setFilterValues(newFilters);
    getListingData(newFilters);
  };

  const renderFilter = () => {
    return (
      filterSections && (
        <ListingFilterWidget
          name={''}
          filters={filterSections}
          onApplyFilter={(v) => {
            onFilterUpdate(v);
          }}
          disabled={lovsLoading || loading}
        />
      )
    );
  };

  const renderMainChildren = () => {
    return ( 
      <>
        <ListingWidget
          title="Providers"
          name="providers"
          orderByAscendingByDefault
          data={tableData}
          tableSettings={{
            headers: headers,
          }}
          usePagination
          disableSelection
          actions={actions}
          loading={loading}
          defaultOrderByColumn="Configuration_Provider_ProviderCode"
          pageContext={PAGE_CONTEXT}
          onPageChange={(filterModel) => {
            const newFilterModel = {
              ...filterValues,
              ...filterModel,
            };
            setFilterValues(newFilterModel);
            return handlePageChange(newFilterModel);
          }}
        />
        {isDrawerOpen && (
          <ProviderDrawer
            open={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            onSuccess={() => {
              getListingData(filterValues);
            }}
            providerDetailsInfo={undefined}
          />
        )}
      </>
    );
  };

  return (
    <StaticLayout
      name={'Providers'}
      leftChildren={renderFilter()}
      mainChildren={renderMainChildren()}
      loading={loading}
    />
  );
};

export default ProvidersPage;
